import React, { Component } from "react";
import {
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardImage,
  MDBCardBody,
  MDBCardTitle,
  MDBCardText,
  MDBBtn,
  MDBBtnGroup,
  MDBIcon,
  MDBBadge,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
} from "mdb-react-ui-kit";

import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import { mfmFramedUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";

import defaultImg from "images/tean02.jpg";
import "./NewsSectionManager.css";

const NEWS_FORMAT_KEY = "ratio:16:9";

/* ───── Helpers de serialización ───── */
const stripDataUrl = (dataUri) =>
  dataUri && dataUri.startsWith("data:image")
    ? dataUri.replace(/^data:image\/[a-zA-Z0-9+.-]+;base64,/, "")
    : dataUri || null;

const toDataUri = (b64) => (b64 ? `data:image/jpeg;base64,${b64}` : "");

const fromBackendItem = (row) => {
  const isActive =
    row?.active === 1 || row?.active === "1" || row?.active === true;
  return {
    id: row?.IDNoticia ?? row?.iDNoticia ?? null,
    slug: row?.slug ?? "",
    title: row?.titulo ?? "",
    text: row?.textoCorto ?? row?.texto ?? "",
    fullText: row?.texto ?? "",
    shortText: row?.textoCorto ?? "",
    date: row?.fecha ?? "",
    image: row?.imageUuid
      ? mfmFramedUrl(row.imageUuid, 320, 180, NEWS_FORMAT_KEY)
      : (row?.base64img ? toDataUri(row.base64img) : ""),
    imageUuid: row?.imageUuid ?? null,
    link: row?.link ?? "",
    sortOrder: row?.sortOrder ?? 0,
    active: isActive,
  };
};

/* ───── Lógica de reordenamiento decimal inteligente ───── */

/**
 * Calcula el nuevo sortOrder para un ítem movido a una posición
 * entre dos vecinos. Usa decimales para minimizar escrituras en DB.
 * NOTA: la lista se ordena DESC (mayor sortOrder = primero), por lo
 * que "before" tiene un sortOrder MAYOR que "after".
 */
function computeDecimalOrder(before, after) {
  if (before == null && after == null) return 1;
  if (before == null) return after + 1;
  if (after == null) return before / 2;
  return (before + after) / 2;
}

/**
 * Determina si los sortOrders necesitan un reindexado completo.
 * Esto pasa cuando la precisión decimal es demasiado fina.
 */
function needsFullReindex(items) {
  if (items.length < 2) return false;
  const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  for (let i = 1; i < sorted.length; i++) {
    const diff = sorted[i].sortOrder - sorted[i - 1].sortOrder;
    if (diff < 1e-10) return true;
  }
  return false;
}

/**
 * Reindexa todos los ítems con sortOrder entero 1..N.
 */
function fullReindex(items) {
  const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  return sorted.map((it, i) => ({ ...it, sortOrder: i + 1 }));
}

const DEFAULT_PER_PAGE = 6;

class NewsSectionManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      filter: "all",
      perPage: DEFAULT_PER_PAGE,
      perPageDraft: String(DEFAULT_PER_PAGE),
      currentPage: 1,
      dragIndex: null,
      overIndex: null,
      showDeleteModal: false,
      itemToDelete: null,
    };
  }

  componentDidMount() {
    this.loadItems();
    this.loadPerPageConfig();
  }

  /* ───── Carga de datos ───── */

  async loadItems() {
    if (this.props.onLoading) this.props.onLoading(true);
    try {
      const response = await doFetch("TEAN", "getNoticias", { method: "GET" });
      const data = await response.json();
      const rawItems = data && data.DATA ? data.DATA : [];
      const items = rawItems
        .map(fromBackendItem)
        .sort((a, b) => b.sortOrder - a.sortOrder);
      this.setState({ items });
    } catch (error) {
      console.error("Error al obtener noticias:", error);
      this.setState({ items: [] });
    } finally {
      if (this.props.onLoading) this.props.onLoading(false);
    }
  }

  async loadPerPageConfig() {
    try {
      const response = await doFetch(
        "TEAN",
        "getSiteContentByPrefix?prefix=NEWS_",
        { method: "GET" }
      );
      const data = await response.json();
      if (data && data.STATE === "SUCCESS" && Array.isArray(data.DATA)) {
        const entry = data.DATA.find(
          (e) => e.contentKey === "NEWS_per_page"
        );
        if (entry && entry.configValue) {
          const val = parseInt(entry.configValue, 10);
          if (val > 0) {
            this.setState({ perPage: val, perPageDraft: String(val) });
          }
        }
      }
    } catch (error) {
      console.error("Error al cargar config NEWS_per_page:", error);
    }
  }

  async savePerPageConfig(value) {
    try {
      const payload = [
        { contentKey: "NEWS_per_page", configValue: String(value) },
      ];
      await doFetch("TEAN", "upsertSiteContentBatch", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      toast.success("Noticias por página actualizado");
    } catch (error) {
      console.error("Error al guardar config NEWS_per_page:", error);
      toast.error("Error al guardar configuración");
    }
  }

  /* ───── Acciones CRUD ───── */

  async toggleActive(item) {
    if (this.props.onLoading) this.props.onLoading(true);
    try {
      const payload = {
        IDNoticia: item.id,
        slug: item.slug,
        titulo: item.title,
        texto: item.fullText,
        textoCorto: item.shortText,
        base64img: null,
        imageUuid: item.imageUuid ?? null,
        fecha: item.date,
        link: item.link,
        sortOrder: item.sortOrder,
        active: item.active ? 0 : 1,
      };
      const response = await doFetch("TEAN", "upsertNoticia", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data && data.STATE === "SUCCESS") {
        toast.success(
          item.active ? "Noticia deshabilitada" : "Noticia habilitada"
        );
        await this.loadItems();
      }
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      toast.error("Error al cambiar estado");
    } finally {
      if (this.props.onLoading) this.props.onLoading(false);
    }
  }

  async deleteItem() {
    const { itemToDelete } = this.state;
    if (!itemToDelete) return;

    if (this.props.onLoading) this.props.onLoading(true);
    try {
      const response = await doFetch(
        "TEAN",
        "deleteNoticia?IDNoticia=" + itemToDelete.id,
        { method: "DELETE" }
      );
      const data = await response.json();
      if (data && data.STATE === "SUCCESS") {
        toast.success("Noticia eliminada");
        await this.loadItems();
      } else {
        toast.error("No se pudo eliminar la noticia");
      }
    } catch (error) {
      console.error("Error al borrar noticia:", error);
      toast.error("Error al eliminar la noticia");
    } finally {
      this.setState({ showDeleteModal: false, itemToDelete: null });
      if (this.props.onLoading) this.props.onLoading(false);
    }
  }

  async disableInsteadOfDelete() {
    const { itemToDelete } = this.state;
    if (!itemToDelete) return;
    this.setState({ showDeleteModal: false, itemToDelete: null });
    await this.toggleActive({ ...itemToDelete, active: true });
  }

  /* ───── Drag & Drop inteligente (decimal) ───── */

  onDragStart = (globalIndex) => (e) => {
    this.setState({ dragIndex: globalIndex, overIndex: globalIndex });
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(globalIndex));
    } catch (_) {}
  };

  onDragEnter = (globalIndex) => (e) => {
    e.preventDefault();
    if (globalIndex !== this.state.overIndex) {
      this.setState({ overIndex: globalIndex });
    }
  };

  onDragOver = (globalIndex) => (e) => {
    e.preventDefault();
    if (globalIndex !== this.state.overIndex) {
      this.setState({ overIndex: globalIndex });
    }
  };

  onDrop = (globalIndex) => async (e) => {
    e.preventDefault();
    const { items } = this.state;
    let fromIdx = parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (!Number.isFinite(fromIdx)) fromIdx = this.state.dragIndex;
    const toIdx = globalIndex;

    this.setState({ dragIndex: null, overIndex: null });

    if (fromIdx == null || fromIdx === toIdx) return;

    const sorted = [...items].sort((a, b) => b.sortOrder - a.sortOrder);
    const movedItem = sorted[fromIdx];
    if (!movedItem) return;

    const rest = sorted.filter((_, i) => i !== fromIdx);
    const insertAt = fromIdx < toIdx ? toIdx - 1 : toIdx;

    const before = insertAt > 0 ? rest[insertAt - 1]?.sortOrder : null;
    const after = insertAt < rest.length ? rest[insertAt]?.sortOrder : null;

    const newOrder = computeDecimalOrder(before, after);

    const updatedItem = { ...movedItem, sortOrder: newOrder };
    const updatedItems = items.map((it) =>
      it.id === movedItem.id ? updatedItem : it
    );

    let itemsToSave;
    if (needsFullReindex(updatedItems)) {
      const reindexed = fullReindex(updatedItems);
      this.setState({ items: reindexed });
      itemsToSave = reindexed.map((it) => ({
        IDNoticia: it.id,
        sortOrder: it.sortOrder,
      }));
    } else {
      this.setState({
        items: updatedItems.sort((a, b) => b.sortOrder - a.sortOrder),
      });
      itemsToSave = [{ IDNoticia: movedItem.id, sortOrder: newOrder }];
    }

    try {
      await doFetch("TEAN", "reorderNoticias", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(itemsToSave),
      });
    } catch (error) {
      console.error("Error al reordenar:", error);
      toast.error("Error al guardar el orden");
      await this.loadItems();
    }
  };

  onDragEnd = () => {
    this.setState({ dragIndex: null, overIndex: null });
  };

  /* ───── Per-page config ───── */

  handlePerPageChange = (e) => {
    this.setState({ perPageDraft: e.target.value });
  };

  handlePerPageBlur = () => {
    const val = parseInt(this.state.perPageDraft, 10);
    if (val > 0 && val !== this.state.perPage) {
      this.setState({ perPage: val, currentPage: 1 });
      this.savePerPageConfig(val);
    } else {
      this.setState({ perPageDraft: String(this.state.perPage) });
    }
  };

  handlePerPageKey = (e) => {
    if (e.key === "Enter") e.target.blur();
  };

  /* ───── Render ───── */

  getFilteredItems() {
    const { items, filter } = this.state;
    const sorted = [...items].sort((a, b) => b.sortOrder - a.sortOrder);
    if (filter === "enabled") return sorted.filter((i) => i.active);
    if (filter === "disabled") return sorted.filter((i) => !i.active);
    return sorted;
  }

  render() {
    const {
      filter,
      perPageDraft,
      perPage,
      currentPage,
      dragIndex,
      overIndex,
      showDeleteModal,
      itemToDelete,
      items,
    } = this.state;
    const { onHandleNavLinks } = this.props;

    const filtered = this.getFilteredItems();
    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const pageItems = filtered.slice(
      (safeCurrentPage - 1) * perPage,
      safeCurrentPage * perPage
    );

    const totalCount = items.length;
    const enabledCount = items.filter((i) => i.active).length;
    const disabledCount = totalCount - enabledCount;

    return (
      <section className="news-admin-section">
        {/* ── Barra superior ── */}
        <div className="nsm-toolbar">
          <h2 className="section-title mb-0">Últimas noticias</h2>

          <div className="nsm-toolbar-controls">
            <MDBBtnGroup size="sm">
              <MDBBtn
                color={filter === "all" ? "primary" : "light"}
                onClick={() =>
                  this.setState({ filter: "all", currentPage: 1 })
                }
              >
                Todas
                <MDBBadge className="ms-1" color="dark" pill>
                  {totalCount}
                </MDBBadge>
              </MDBBtn>
              <MDBBtn
                color={filter === "enabled" ? "success" : "light"}
                onClick={() =>
                  this.setState({ filter: "enabled", currentPage: 1 })
                }
              >
                <MDBIcon fas icon="eye" className="me-1" />
                Habilitadas
                <MDBBadge className="ms-1" color="dark" pill>
                  {enabledCount}
                </MDBBadge>
              </MDBBtn>
              <MDBBtn
                color={filter === "disabled" ? "secondary" : "light"}
                onClick={() =>
                  this.setState({ filter: "disabled", currentPage: 1 })
                }
              >
                <MDBIcon fas icon="eye-slash" className="me-1" />
                Deshabilitadas
                <MDBBadge className="ms-1" color="dark" pill>
                  {disabledCount}
                </MDBBadge>
              </MDBBtn>
            </MDBBtnGroup>

            <div className="nsm-per-page">
              <label className="text-muted small me-1">Por página:</label>
              <input
                type="number"
                className="form-control form-control-sm nsm-per-page-input"
                min="1"
                max="50"
                value={perPageDraft}
                onChange={this.handlePerPageChange}
                onBlur={this.handlePerPageBlur}
                onKeyDown={this.handlePerPageKey}
              />
            </div>
          </div>
        </div>

        {/* ── Grid de tarjetas ── */}
        <MDBRow className="row-cols-1 row-cols-md-3 g-4 mt-1">
          {/* Tarjeta "+ Nueva noticia" */}
          <MDBCol>
            <MDBCard
              className="h-100 nsm-add-card"
              onClick={() => {
                if (onHandleNavLinks)
                  onHandleNavLinks(null, "/noticia/nueva");
              }}
            >
              <MDBCardBody className="d-flex flex-column justify-content-center align-items-center">
                <MDBIcon
                  icon="plus"
                  size="4x"
                  className="text-muted mb-2"
                />
                <span className="text-muted fw-bold">Nueva noticia</span>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>

          {/* Tarjetas de noticias */}
          {pageItems.map((item) => {
            const globalIdx = items
              .slice()
              .sort((a, b) => b.sortOrder - a.sortOrder)
              .findIndex((i) => i.id === item.id);
            const isDragging = dragIndex === globalIdx;
            const isOver = overIndex === globalIdx;

            return (
              <MDBCol key={item.id}>
                <MDBCard
                  className={`h-100 nsm-card ${!item.active ? "nsm-card-disabled" : ""} ${isDragging ? "nsm-dragging" : ""} ${isOver ? "nsm-over" : ""}`}
                  draggable
                  onDragStart={this.onDragStart(globalIdx)}
                  onDragEnter={this.onDragEnter(globalIdx)}
                  onDragOver={this.onDragOver(globalIdx)}
                  onDrop={this.onDrop(globalIdx)}
                  onDragEnd={this.onDragEnd}
                >
                  <div className="position-relative">
                    <MDBCardImage
                      src={item.image || defaultImg}
                      position="top"
                      alt={item.title}
                      className="nsm-card-img"
                    />
                    <MDBBadge
                      className="nsm-status-badge"
                      color={item.active ? "success" : "secondary"}
                    >
                      {item.active ? "Activa" : "Inactiva"}
                    </MDBBadge>
                    <div className="nsm-drag-handle">
                      <MDBIcon fas icon="grip-vertical" />
                    </div>
                  </div>

                  <MDBCardBody className="d-flex flex-column">
                    <MDBCardTitle
                      className="nsm-card-title"
                      onClick={() => {
                        if (item.slug && onHandleNavLinks) {
                          onHandleNavLinks(null, `/noticia/${item.slug}`);
                        }
                      }}
                    >
                      {item.title}
                    </MDBCardTitle>
                    <MDBCardText className="nsm-card-text flex-grow-1">
                      {item.text}
                    </MDBCardText>

                    <div className="nsm-card-footer">
                      <small className="text-muted">{item.date}</small>
                      <div className="nsm-card-actions">
                        <MDBBtn
                          size="sm"
                          floating
                          color={item.active ? "success" : "secondary"}
                          title={
                            item.active ? "Deshabilitar" : "Habilitar"
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            this.toggleActive(item);
                          }}
                        >
                          <MDBIcon
                            fas
                            icon={item.active ? "eye" : "eye-slash"}
                          />
                        </MDBBtn>
                        <MDBBtn
                          size="sm"
                          floating
                          color="danger"
                          title="Eliminar"
                          className="ms-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.setState({
                              showDeleteModal: true,
                              itemToDelete: item,
                            });
                          }}
                        >
                          <MDBIcon fas icon="trash" />
                        </MDBBtn>
                      </div>
                    </div>
                  </MDBCardBody>
                </MDBCard>
              </MDBCol>
            );
          })}
        </MDBRow>

        {/* ── Paginador ── */}
        {totalPages > 1 && (
          <nav className="d-flex justify-content-center mt-4">
            <ul className="pagination pagination-sm">
              <li
                className={`page-item ${safeCurrentPage <= 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    this.setState({ currentPage: safeCurrentPage - 1 })
                  }
                >
                  &laquo;
                </button>
              </li>
              {Array.from({ length: totalPages }, (_, i) => (
                <li
                  key={i}
                  className={`page-item ${i + 1 === safeCurrentPage ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => this.setState({ currentPage: i + 1 })}
                  >
                    {i + 1}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${safeCurrentPage >= totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    this.setState({ currentPage: safeCurrentPage + 1 })
                  }
                >
                  &raquo;
                </button>
              </li>
            </ul>
          </nav>
        )}

        {/* ── Modal de borrado (con opción de deshabilitar) ── */}
        <MDBModal
          open={showDeleteModal}
          onClose={() =>
            this.setState({ showDeleteModal: false, itemToDelete: null })
          }
          tabIndex="-1"
        >
          <MDBModalDialog centered>
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>
                  <MDBIcon
                    fas
                    icon="exclamation-triangle"
                    className="text-warning me-2"
                  />
                  Confirmar eliminación
                </MDBModalTitle>
                <MDBBtn
                  className="btn-close"
                  color="none"
                  onClick={() =>
                    this.setState({
                      showDeleteModal: false,
                      itemToDelete: null,
                    })
                  }
                />
              </MDBModalHeader>
              <MDBModalBody>
                <p>
                  ¿Está seguro de que desea eliminar la noticia{" "}
                  <strong>"{itemToDelete?.title}"</strong>?
                </p>
                <p className="text-muted small">
                  Esta acción no se puede deshacer. Si solo desea ocultarla
                  temporalmente, puede deshabilitarla en su lugar.
                </p>
              </MDBModalBody>
              <MDBModalFooter className="d-flex justify-content-between">
                <MDBBtn
                  color="secondary"
                  outline
                  onClick={() =>
                    this.setState({
                      showDeleteModal: false,
                      itemToDelete: null,
                    })
                  }
                >
                  Cancelar
                </MDBBtn>
                <div>
                  {itemToDelete?.active && (
                    <MDBBtn
                      color="warning"
                      className="me-2"
                      onClick={() => this.disableInsteadOfDelete()}
                    >
                      <MDBIcon fas icon="eye-slash" className="me-1" />
                      Deshabilitar
                    </MDBBtn>
                  )}
                  <MDBBtn
                    color="danger"
                    onClick={() => this.deleteItem()}
                  >
                    <MDBIcon fas icon="trash" className="me-1" />
                    Eliminar
                  </MDBBtn>
                </div>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      </section>
    );
  }
}

export default NewsSectionManager;
